import { useState } from 'react'
import { useAppSelector } from 'app-redux/hooks'

import { useOwnsDeck } from 'hooks/usePermissions'

import { usePinnedInteractions } from 'hooks/usePinnedSidecar'

import Editor from 'components/formElements/richText/Editor'
import PhatButton from 'components/formElements/PhatButton'
import CommentContainer from 'components/deckPage/CommentContainer'
import PhatDropdown from 'components/formElements/PhatDropdown'
import DescriptionSettings from 'components/deckPage/settings/forms/Description'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import DeckHelpInfo from 'components/deckPage/DeckHelpInfo'

import { isEmpty } from 'utils/Deltas'

import styles from './pinnedCommentContainer.module.scss'

const PinnedCommentContainer = () => {
  const deckHelp = useAppSelector(state => state.deck.deckHelp)
  const description = useAppSelector(state => state.deck.description)
  const ownsDeck = useOwnsDeck()

  const [pinnedInteractionStatus, setPinnedInteractionStatus] = usePinnedInteractions()

  const [editing, setEditing] = useState(false)
  const [editedDescription, setEditedDescription] = useState(description)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const handleWarningCancel = () => {
    if (editedDescription !== description) return setConfirmCancel(true)

    setEditing(false)
  }

  const handleCancel = () => {
    setEditing(false)
    setConfirmCancel(false)
    setEditedDescription(description)
  }

  if (!pinnedInteractionStatus.locked) return null

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <PhatDropdown
          className={styles.viewPicker}
          disabled={editing}
          value={pinnedInteractionStatus.type}
          options={[
            {
              label: 'Deck help request',
              icon: 'help circle',
              onClick: () => setPinnedInteractionStatus({ ...pinnedInteractionStatus, type: 'deckHelp' }),
              id: 'deckHelp',
              hidden: !deckHelp,
            },
            {
              label: 'Description',
              icon: 'newspaper outline',
              onClick: () => setPinnedInteractionStatus({ ...pinnedInteractionStatus, type: 'description' }),
              id: 'description',
            },
            {
              label: 'Comments',
              icon: 'comment outline',
              onClick: () => setPinnedInteractionStatus({ ...pinnedInteractionStatus, type: 'comments' }),
              id: 'comments',
            },
          ]}
        />

        {ownsDeck && pinnedInteractionStatus.type === 'description' && (
          <>
            {!editing && (
              <PhatButton icon="edit outline" onClick={() => setEditing(true)} className={styles.edit}>
                Edit
              </PhatButton>
            )}

            {editing && (
              <PhatButton icon="close" onClick={handleWarningCancel}>
                Cancel
              </PhatButton>
            )}
          </>
        )}

        <PhatButton
          disabled={editing}
          icon="columns"
          onClick={() => setPinnedInteractionStatus({ ...pinnedInteractionStatus, locked: false })}>
          Unpin
        </PhatButton>
      </div>

      {pinnedInteractionStatus.type === 'description' && (
        <>
          {editing ? (
            <DescriptionSettings
              description={editedDescription}
              editorClassName={styles.editor}
              onDescriptionChange={setEditedDescription}
              onDescriptionSaved={() => setEditing(false)}
            />
          ) : (
            <>
              {isEmpty(description) && <p className={styles.empty}>This deck has no description</p>}
              {!isEmpty(description) && <Editor className={styles.description} value={description} readOnly />}
            </>
          )}
        </>
      )}

      {pinnedInteractionStatus.type === 'comments' && (
        <CommentContainer header="Have any suggestions?" className={styles.comment} />
      )}

      {pinnedInteractionStatus.type === 'deckHelp' && <DeckHelpInfo />}

      <ConfirmDeleteModal
        open={confirmCancel}
        buttonText="Disregard edits"
        message="Are you sure you want to close the editor? Any changes you've made will be discarded."
        header="You have unsaved changes"
        onConfirm={handleCancel}
        onClose={() => setConfirmCancel(false)}
      />
    </div>
  )
}

export default PinnedCommentContainer
